<template>
  <div class="card" :class="classNames">
    <div class="card_item">
      <Transition name="card-appear" mode="out-in" appear>
        <RouterLink class="card_img" :to="detailLinkParams" :key="card.cardCode">
          <CardPicture
            :game="card.currentGame || card.issuedGame"
            :design="card.currentDesign"
            :partner="card.partner"
            hovering
          />
        </RouterLink>
      </Transition>

      <div class="card_info">
        <span class="card_info-bank-label" v-if="bankLabel">{{bankLabel}}</span>
        <RouterLink class="card_info-heading" v-if="layout === 'list'" :to="detailLinkParams" :key="card.cardCode">
          <h2 class="heading2" v-text="cardName" />
        </RouterLink>
        <div class="card_info-heading" v-else>
          <h2 class="heading2" v-text="cardName" />
        </div>
        <div class="card_info-status">
          <span
            class="card-status"
            :class="!isActive && 'card-status__inactive'"
            v-text="cardStatusText"
          />
        </div>
      </div>

      <CardDetails
        v-if="isActive && layout === 'detail'"
        :card="card"
        @game-change="initGameChange"
        @design-change="initDesignChange"
      />

      <div class="card_history">
        <CardAccruals v-if="isActive" :card="card" />
        <p class="card-message" v-else v-html="cardStatusMessage"/>
      </div>

      <div class="card_button-detail" v-if="layout === 'list'">
        <Button
          variant="outlined"
          type="router-link"
          :to="detailLinkParams"
          @click.native="onDetailsClick"
        >
          {{ $t('Детали') }}
        </Button>
      </div>

      <template v-if="isActive">
        <div class="card_button-main">
          <Button
            type="a"
            :href="depositUrl"
            target="_blank"
            @click.native="onDepositClick"
          >
            {{ $t('Пополнить') }}
          </Button>
        </div>
        <div class="card_footer-text">
          <p class="card-note">
            * {{ $t('Пополнение осуществляется без комиссии со стороны «%(bankLabel)s»', {bankLabel}) }} <br />
          </p>
        </div>
      </template>
      <div
        class="extra-bonus-place"
        v-if="extraBonus && (layout === 'list')"
      >
        <ExtraBonusCircle
          :game="cardGame"
          :card="card"
          :extraBonus="extraBonus"
          :partner="partner"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { State } from 'vuex-class'
import ExtraBonusCircle from '@/components/ExtraBonus/ExtraBonusCircle'

import Button from '@/common/components/Button'
import {
  CARD_NAMES,
  CARD_STATUS,
  CARD_NAMES_BY_GAME,
  VIRTUAL_CURRENCY_GROUP,
  SUPPORT_PORTAL_LINK_URL,
  PARTNER_DATA_BY_NAME
} from '@/constants'
import { getBankSupportUrl, getDepositUrl } from '@/helpers'

import CardDetails from './CardDetails'
import CardPicture from './CardPicture'
import CardAccruals from './CardAccruals'

@Component({
  components: { Button, CardDetails, CardPicture, CardAccruals, ExtraBonusCircle }
})
class Card extends Vue {
  @State('country') currentCountry
  @State('designs') designs

  @Prop({ type: Object, required: true }) card

  @Prop({
    type: String,
    default: 'list',
    validator: layout => ['list', 'detail'].includes(layout)
  }) layout

  get bankLabel () {
    if (!this.card.partner) return null
    if (!PARTNER_DATA_BY_NAME[this.card.partner]) return null
    return PARTNER_DATA_BY_NAME[this.card.partner].label
  }

  get partner () {
    return this.card.partner
  }

  get depositUrl () {
    return getDepositUrl(this.card.partner)
  }

  /**
   * Card active status
   * PARTNER_BLOCK card displayed as active (business requirement)
   * @type {boolean}
   */
  get isActive () {
    return this.card.status !== CARD_STATUS.UNBOUND
  }

  /**
   * Card status text
   * @type {string}
   */
  get cardStatusText () {
    return {
      [CARD_STATUS.BOUND]: this.$t('Активна'),
      [CARD_STATUS.UNBOUND]: this.$t('Неактивна'),
      // PARTNER_BLOCK card displayed as active (business requirement)
      [CARD_STATUS.PARTNER_BLOCK]: this.$t('Активна')
    }[this.card.status]
  }

  /**
   * Card status message
   * @type {string}
   */
  get cardStatusMessage () {
    return {
      [CARD_STATUS.UNBOUND]: this.$t('Данная карта была перепривязана к другому аккаунту. %(link_start)sОбратиться в поддержку%(link_stop)s.', {
        link_start: `<a class='link' href="${SUPPORT_PORTAL_LINK_URL}" target="_blank">`,
        link_stop: '</a>'
      }),
      // this code in the current business requirements is not achievable
      [CARD_STATUS.PARTNER_BLOCK]: this.$t(
        'Карта заблокирована. %(link_start)sОбратиться в службу поддержки Альфа Банк%(link_stop)s.', {
          link_start: `<a class='link' href="${getBankSupportUrl(this.currentCountry)}" target="_blank">`,
          link_stop: '</a>'
        })
    }[this.card.status]
  }

  /**
   * Human readable card name
   * @type {string}
   */
  get cardName () {
    const cardName = CARD_NAMES[this.card.cardType]
    const fallbackName = this.$t(CARD_NAMES_BY_GAME[this.card.currentGame || this.card.issuedGame])
    return cardName || fallbackName
  }

  get cardGame () {
    return this.card.currentGame || this.card.issuedGame
  }

  /**
   * Card Detail router link params
   * @type {Object}
   */
  get detailLinkParams () {
    return { name: 'card_detail', params: { cardCode: this.card.cardCode } }
  }

  /**
   * CSS Class names
   * @type {Object}
   */
  get classNames () {
    const { currentGame } = this.card
    return {
      'card__detail': this.layout === 'detail',
      'card__inactive': !this.isActive,
      [`card__${currentGame}`]: !!currentGame
    }
  }

  /**
   * Total sum of virtuals money accruals by card
   * @returns {Number}
   */
  get moneyAccrualsSum () {
    return this.card.accrualSums
      .filter(a => a.accrualGroup === VIRTUAL_CURRENCY_GROUP)
      .reduce((acc, a) => acc + a.value, 0)
  }

  /**
   * CSS modificator for accruals sum.
   * @type {string}
   */
  get sumClassModifier () {
    const ranges = [
      [1, 599], [600, 999], [1000, 1999], [2000, 2999], [3000, 3999],
      [4000, 4999], [5000, 7499], [7500, 9999], [10000, 14999],
      [15000, Infinity]
    ]
    const range = ranges.find(([min, max]) => (this.moneyAccrualsSum >= min && this.moneyAccrualsSum <= max))
    return range ? range.join('-').toLowerCase() : null
  }

  get extraBonus () {
    return this.card.extraBonus
  }

  /**
   * @emits <game-change>
   */
  @Emit('game-change')
  initGameChange () {
    return this.card
  }

  /**
   * @emits <design-change>
   */
  @Emit('design-change')
  initDesignChange () {
    return this.card
  }

  onDetailsClick () {
    this.$analytics.trackCardDetailsButtonClick({ screenId: this.$route.name, game: this.card.currentGame })
  }

  onDepositClick () {
    this.$analytics.trackDepositButtonClick({ screenId: this.$route.name, game: this.card.currentGame })
  }
}

export default Card
</script>

<style lang="scss" src="./styles/card.scss"></style>
<style lang="scss" src="./styles/card-status.scss"></style>
<style lang="scss" src="./styles/game-name.scss"></style>
<style lang="scss" src="./styles/card-message.scss"></style>

<style lang="scss">
.card-appear {
  &-enter,
  &-leave-to {
    opacity: 0;
    transform: scale(0.9);
  }

  &-enter-active,
  &-leave-active {
    transition: transform $duration ease-in-out,
                opacity $duration ease-in-out;
  }
}

.card-info-appear {

}
.extra-bonus-place {
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: contain;
  content: '';
  display: flex;
  justify-self: end;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  grid-column-start: 1;
  grid-column-end: 2;
  grid-row-start: 7;
  grid-row-end: 7;
  @include media_tablet {
    grid-column-start: 1;
    grid-column-end: 3;
  }
  @include media_desktop {
    grid-column: 4;
    grid-row-start: 1;
    grid-row-end: 5;
    padding: 0 0 0 40px;
  }
}
</style>
